<template>
  <div id="multicastGroups">
    <!-- the user has no cluster => display the warning page -->
    <!-- check true and false to not display anything while it's null -->
    <no-groups v-if="$store.getters.userHasClusters===false" groups="clusters" />

    <template v-if="$store.getters.userHasClusters===true">
      <select-group all group="cluster" />

      <card :title="$t('multicastGroups:filter')" :title-small="titleSmallNbFilters" :visible="false" collapsible>
        <div slot="card-body">
          <div class="form-row">
            <div class="form-group col-sm-8">
              <label for="mcAddr" class="control-label">{{ $t('multicastGroups:mcAddr') | capitalize }}</label>
              <multiselect id="mcAddr"
                           v-model="filterMcAddr"
                           :options="mcAddrList"
                           :multiple="true"
                           :internal-search="false"
                           :clear-on-select="false"
                           :close-on-select="true"
                           :show-labels="false"
                           :options-limit="maxPageSizePerFilterRequest + 1"
                           :placeholder="$t('multicastGroups:typeAndSelectGroup') | capitalize"
                           track-by="mcAddr"
                           label="mcAddr"
                           @search-change="getMcAddrList"
              >
                <span slot="noResult">{{ $t('multicastGroups:noData') | capitalize }}</span>
              </multiselect>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group col-sm-4">
              <label for="name" class="control-label">{{ $t('multicastGroups:name') | capitalize }}</label>
              <input id="name"
                     v-model="filterName"
                     v-validate="'text'"
                     :data-vv-as="$t('multicastGroups:name')"
                     :class="{ 'is-invalid': errors.has('name') }"
                     type="text"
                     name="name"
                     class="form-control"
              >
              <div class="invalid-feedback">{{ errors.first('name') }}</div>
            </div>

            <div class="form-group col-sm-4">
              <label for="freq" class="control-label">{{ $t('multicastGroups:freq') | capitalize }}</label>
              <input id="freq"
                     v-model="filterFreq"
                     v-validate="'number'"
                     :data-vv-as="$t('multicastGroups:freq')"
                     :class="{ 'is-invalid': errors.has('freq') }"
                     type="text"
                     name="freq"
                     class="form-control"
              >
              <small class="form-text text-muted">{{ $t('multicastGroups:freqUnit') | capitalize }}</small>
              <div class="invalid-feedback">{{ errors.first('freq') }}</div>
            </div>

            <div class="form-group col-sm-4">
              <label for="dataRate" class="control-label">{{ $t('multicastGroups:dataRate') | capitalize }}</label>
              <input id="dataRate"
                     v-model="filterDataRate"
                     v-validate="'text'"
                     :data-vv-as="$t('multicastGroups:dataRate')"
                     :class="{ 'is-invalid': errors.has('filterDataRate') }"
                     type="text"
                     name="dataRate"
                     class="form-control"
              >
              <div class="invalid-feedback">{{ errors.first('dataRate') }}</div>
            </div>
          </div>
        </div>

        <div slot="card-footer">
          <button id="filterButton" class="btn btn-primary" @click="filter()">{{ $t('multicastGroups:search') | capitalize }}</button>
        </div>
      </card>

      <grid id="multicastGroupsGrid"
            :title="$t('multicastGroups:multicastGroups')"
            :columns.sync="columns"
            :paginated-dto="multicastGroups"
            :manual-export="exportMulticastGroups"
            @load="filter"
      >
        <router-link slot="mcAddr" slot-scope="props" :to="{name: 'multicastGroup', params: {id: props.dto.id}}">{{ props.dto.mcAddr }}</router-link>
        <template slot="cluster" slot-scope="props">{{ props.dto.cluster ? props.dto.cluster.name : '👻' }}</template>
      </grid>
    </template>
  </div>
</template>

<script>
import { isEqual, debounce, isEmpty, map, cloneDeep, remove, camelCase } from 'lodash-es'
import { mapGetters } from 'vuex'
import to from '@/modules/await-to-js'
import constants from '@/configuration/constants'

import NoGroups from '@/components/page/errors/NoGroups'

export default {
  titleKey: 'multicastGroups',

  components: { NoGroups },

  data () {
    return {
      // List of mc addr for filters
      mcAddrList: [],
      // Filters
      filterMcAddr: [],
      filterName: '',
      filterFreq: '',
      filterDataRate: '',
      nbFilters: 0,

      columns: [
        { text: this.$t('multicastGroups:mcAddr'), slot: 'mcAddr', sort: 'mcAddr' },
        { text: this.$t('multicastGroups:name'), formatter: multicastGroup => multicastGroup.name, sort: 'name' },
        { text: this.$t('multicastGroups:fcntDown'), formatter: multicastGroup => multicastGroup.fcntDown },
        { text: this.$t('multicastGroups:freq'), formatter: multicastGroup => multicastGroup.freq + ' MHz' },
        { text: this.$t('multicastGroups:dataRate'), formatter: multicastGroup => multicastGroup.dataRate },
        { text: this.$t('multicastGroups:nbEndDevices'), formatter: multicastGroup => multicastGroup.endDevices ? multicastGroup.endDevices.length : '-' }
      ],
      multicastGroups: {}
    }
  },

  computed: {
    ...mapGetters(['selectedCluster', 'selectedClusterId', 'multicastGroupsFilters']),
    maxPageSizePerFilterRequest () { return constants.maxPageSizePerFilterRequest },

    titleSmallNbFilters () {
      return this.nbFilters > 0 ? this.$t('multicastGroups:appliedFilter', { count: this.nbFilters }) : ''
    }
  },

  watch: {
    // When selectedCluster change, get list of multicastGroups for this cluster
    selectedCluster (newCluster, oldCluster) {
      // If cluster change, reset filters
      if (!isEqual(newCluster, oldCluster)) {
        this.filterMcAddr = []
        this.filterName = ''
        this.filterFreq = ''
        this.filterDataRate = ''
      }

      // Get page 1
      this.filter({ page: 1 })

      // Get MacAddr filter list
      this.getMcAddrList()
    }
  },

  created () {
    // Set saved filters
    this.filterMcAddr = this.multicastGroupsFilters.mcAddr || []
    this.filterName = this.multicastGroupsFilters.name || ''
    this.filterFreq = this.multicastGroupsFilters.freq || ''
    this.filterDataRate = this.multicastGroupsFilters.dataRate || ''

    // Only if cluster is empty (ie all clusters selected)
    if (isEmpty(this.selectedCluster) && this.selectedClusterId === -1) {
    // if (isEmpty(this.selectedCluster)) {
      this.filter()
      this.getMcAddrList()
    }
  },

  methods: {
    getMcAddrList: debounce(async function (searchQuery = '') {
      let search = []

      // Filter by selected cluster
      if (!isEmpty(this.selectedCluster)) search = this.$utils.addSearch(search, 'eq', this.selectedCluster.id, 'cluster.id')
      if (searchQuery) search = this.$utils.addSearch(search, 'like', '*' + searchQuery.toUpperCase() + '*', 'mcAddr')
      if (search.length > 0) search = JSON.stringify(search.length === 1 ? search[0] : this.$utils.formatSearch('and', search))

      let error, response
      [error, response] = await to(this.$api.getMulticastGroups(this.$store.getters.application, { pageSize: constants.maxPageSizePerFilterRequest, sort: '+mcAddr', fields: 'mcAddr', search }))
      if (error || !response) return

      // Add ... if all results are not displayed
      if (response.data.count < response.data.totalCount) {
        response.data.list.push({ mcAddr: '...', $isDisabled: true })
      }
      this.mcAddrList = response.data.list
    }, 500),

    async filter (options = {}) {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      // Save filters
      this.$store.commit('setMulticastGroupsFilters', {
        mcAddr: this.filterMcAddr,
        name: this.filterName,
        freq: this.filterFreq,
        dataRate: this.filterDataRate
      })

      // Add criteria
      let search = []
      if (this.filterMcAddr.length > 0) {
        const mcAddrs = map(this.filterMcAddr, group => group.mcAddr)
        search = this.$utils.addSearch(search, 'in', mcAddrs, 'mcAddr')
      }

      if (this.filterName) search = this.$utils.addSearch(search, 'like', '*' + this.filterName + '*', 'name')
      if (this.filterFreq) search = this.$utils.addSearch(search, 'eq', Number(this.filterFreq), 'freq')
      if (this.filterDataRate) search = this.$utils.addSearch(search, 'eq', this.filterDataRate, 'dataRate')

      // Do not count fleet filter in filters count
      this.nbFilters = search.length

      // Filter by selected cluster
      if (!isEmpty(this.selectedCluster)) search = this.$utils.addSearch(search, 'eq', this.selectedCluster.id, 'cluster.id')

      // Format criteria
      if (search.length > 0) options.search = JSON.stringify(search.length === 1 ? search[0] : this.$utils.formatSearch('and', search))

      // Get filtered data
      this.getMulticastGroups(options)
    },

    async getMulticastGroups (options) {
      const params = this.$utils.getParams({ sort: '+mcAddr' }, options, this.queryPageParams)
      let error, response
      [error, response] = await to(this.$api.getMulticastGroups(this.$store.getters.application, params))
      if (error || !response) return

      this.multicastGroups = response.data

      // We do it after getting data to prevent the column to appear before the data
      const columns = cloneDeep(this.columns)
      // Remove cluster colum
      remove(columns, c => c.slot === 'cluster')
      if (isEmpty(this.selectedCluster)) {
        // All fleets selected, add fleet colum
        columns.splice(2, 0, { text: this.$t('multicastGroups:cluster'), slot: 'cluster' })
      }
      this.columns = columns
    },

    // ============= Export methods

    // Export current page, but without keys (Mantis #10032)
    async exportMulticastGroups () {
      const mcGroupsToExport = cloneDeep(this.multicastGroups.list).map(group => {
        delete group.appSKey
        delete group.nwkSKey
        return group
      })

      this.$utils.exportCsv(mcGroupsToExport, camelCase(this.$t('multicastGroups:multicastGroups')))
    }
  }
}
</script>
